import {
  IrisBankAccount,
  IrisBalance,
  IrisConsent,
  IrisTransaction,
  IrisConsentStatus,
  IrisCreditDebitIndicator,
  TransactionRow,
  IrisUser,
} from '@app/core/types/iris-report';
import { Decimal } from '@app/core/utils/decimal';
import moment from 'moment';


export class BankAccountView {
  /**
   * Priority
   */
  private static readonly BALANCE_PRIORITY: string[] = [
    'interimavailable',
    'expected',
    'authorised',
    'forwardavailable',
    'information',
    'closingbooked',
    'openingbooked',
  ];

  account: IrisBankAccount;
  brand: string;
  user: IrisUser;
  balance: number | null;
  balanceCurrency: string;
  balanceReferenceDate: string | null;
  balances: IrisBalance[];
  consent: IrisConsent | null;
  consentStatusColor!: string;
  consentStatusLabel!: string;
  consentRawStatus!: string;
  transactions: TransactionRow[];
  totalIncoming31Days: number;
  totalOutgoing31Days: number;
  expanded: boolean;
  isDifferentBrand: boolean;
  pagedTransactions: TransactionRow[];
  pageSize: number;
  currentPage: number;

  get totalPages(): number {
    return Math.ceil(this.transactions.length / this.pageSize);
  }

  private static readonly BALANCE_PERMISSIONS: Set<string> = new Set([
    'BALANCES',
    'ReadBalances',
  ]);

  private static readonly TRANSACTION_PERMISSIONS: Set<string> = new Set([
    'TRANSACTIONS',
    'ReadTransactionsCredits',
    'ReadTransactionsDebits',
    'ReadTransactionsDetail',
  ]);

  get hasBalancesPermission(): boolean {
    return this.consent?.permissions?.some((p) =>
      BankAccountView.BALANCE_PERMISSIONS.has(p.name)
    ) ?? false;
  }

  get hasTransactionsPermission(): boolean {
    return this.consent?.permissions?.some((p) =>
      BankAccountView.TRANSACTION_PERMISSIONS.has(p.name)
    ) ?? false;
  }

  get isConsentActive(): boolean {
    return this.consent.status === IrisConsentStatus.VALID;
  }

  get consentChipLabelKeys(): string[] {
    if (!this.isConsentActive) {
      return [this.consentStatusLabel];
    }

    const keys: string[] = [];
    if (this.hasBalancesPermission) {
      keys.push('bankInfo.permissions.balances');
    }
    if (this.hasTransactionsPermission) {
      keys.push('bankInfo.permissions.transactions');
    }

    return keys.length > 0 ? keys : ['bankInfo.permissions.none'];
  }

  constructor(
    account: IrisBankAccount,
    user: IrisUser,
    clientBrandCode: string
  ) {
    this.account = account;
    this.brand = user.brand;
    this.user = user;
    this.isDifferentBrand = user.brand !== clientBrandCode;

    this.consent = [...(account.consents ?? [])]
      .sort((a, b) => moment(b.created_at).valueOf() - moment(a.created_at).valueOf())[0];
    console.log(this.consent)

    this.resolveConsentDisplay();

    const bestBalance = this.findBestBalance();

    this.balance = bestBalance?.amount ?? null;
    this.balanceCurrency = bestBalance?.currency ?? account.currency;
    this.balanceReferenceDate = bestBalance?.referenceDate ?? null;
    this.balances = account.balances || [];
    this.transactions = (account.transactions || []).map((txn) => ({
      ...txn,
      counterpartyName: this.resolveCounterpartyName(txn),
      counterpartyIban: this.resolveCounterpartyIban(txn),
    }));
    this.currentPage = 0;
    this.pageSize = 20;
    this.pagedTransactions = this.transactions.slice(0, this.pageSize);
    this.expanded = false;

    const { totalIncoming, totalOutgoing } = this.calculateTurnover();
    this.totalIncoming31Days = totalIncoming;
    this.totalOutgoing31Days = totalOutgoing;
  }

  private findBestBalance(): IrisBalance | undefined {
    if (!this.account.balances?.length) return undefined;

    for (const type of BankAccountView.BALANCE_PRIORITY) {
      const matchingBalances = this.account.balances.filter(
        (b) => b.balanceType.toLowerCase() === type
      );

      if (matchingBalances.length > 0) {
        return matchingBalances.sort((a, b) =>
          moment(b.referenceDate).diff(moment(a.referenceDate))
        )[0];
      }
    }
    return this.account.balances
      .slice()
      .sort((a, b) => moment(b.referenceDate).diff(moment(a.referenceDate)))[0];
  }

  private resolveConsentDisplay(): void {
    if (!this.consent) {
      this.consentStatusColor = 'warn';
      this.consentStatusLabel = 'bankInfo.consent.noConsent';
      this.consentRawStatus = '';
      return;
    }

    this.consentRawStatus = this.consent.status || '';

    const normalizedStatus = this.consent.status?.toLowerCase();

    const statusMap: { [key: string]: { color: string; label: string } } = {
      valid: {
        color: 'accent',
        label: 'bankInfo.consent.statusValid',
      },
      invalid: {
        color: 'warn',
        label: 'bankInfo.consent.statusInvalid',
      }
    };

    const config = statusMap[normalizedStatus] || {
      color: 'warn',
      label: 'bankInfo.consent.statusUnknown',
    };
    this.consentStatusColor = config.color;
    this.consentStatusLabel = config.label;
  }

  private calculateTurnover(): {
    totalIncoming: number;
    totalOutgoing: number;
  } {
    const now = new Date();
    const thirtyOneDaysAgo = new Date(now.getTime() - 31 * 24 * 60 * 60 * 1000);

    const incomingAmounts: number[] = [];
    const outgoingAmounts: number[] = [];

    for (const txn of this.transactions) {
      const txnDate = new Date(txn.bookingDate);
      if (txnDate >= thirtyOneDaysAgo) {
        const amount = Math.abs(txn.transactionAmount.amount);
        if (txn.creditDebitIndicator === IrisCreditDebitIndicator.CREDIT) {
          incomingAmounts.push(amount);
        } else {
          outgoingAmounts.push(amount);
        }
      }
    }

    return {
      totalIncoming: Decimal.sumNumbers(...incomingAmounts),
      totalOutgoing: Decimal.sumNumbers(...outgoingAmounts),
    };
  }

  private resolveCounterpartyName(txn: IrisTransaction): string {
    return txn.creditDebitIndicator === IrisCreditDebitIndicator.CREDIT
      ? txn.debtorName || '-'
      : txn.creditorName || '-';
  }

  private resolveCounterpartyIban(txn: IrisTransaction): string {
    return txn.creditDebitIndicator === IrisCreditDebitIndicator.CREDIT
      ? txn.debtorAccount?.iban || '-'
      : txn.creditorAccount?.iban || '-';
  }

  public changePage(pageIndex: number, pageSize: number): void {
    this.currentPage = pageIndex;
    this.pageSize = pageSize;
    const start = pageIndex * pageSize;
    this.pagedTransactions = this.transactions.slice(start, start + pageSize);
  }

  get turnover(): number {
    return Decimal.sumNumbers(
      this.totalIncoming31Days,
      this.totalOutgoing31Days
    );
  }
}
